import React from 'react';
import { FolderTree, Users, DatabaseBackup, ShieldCheck, LogOut } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export default function WelcomeScreen({
  onManagePatterns,
  onUserManagement,
  onBackupManagement,
  onSiteModeChange,
  onLogout,
  canManagePatterns = false,
  canAccessUserManagement = false,
  canAccessBackupManagement = false,
  canManageSiteMode = false,
  siteMode = 'dev',
  siteModeUpdatedAt = null,
  siteModeUpdatedBy = null,
}) {
  const enabledCards = [
    canManagePatterns,
    canAccessUserManagement,
    canAccessBackupManagement,
  ].filter(Boolean).length;

  const cardColumnsClass = enabledCards >= 4
    ? 'md:grid-cols-2 lg:grid-cols-4'
    : enabledCards === 3
      ? 'md:grid-cols-3'
      : enabledCards === 2
        ? 'md:grid-cols-2'
        : 'md:grid-cols-1';

  return (
    <div className="flex items-center justify-center h-full bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-5xl px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">PatternEngine</h1>
          <p className="text-lg text-gray-600">Professional CAD for Bespoke Tailoring</p>

          {canManageSiteMode && (
            <div className="mt-5 mx-auto max-w-2xl text-left border rounded-xl bg-white/90 p-4" data-testid="welcome-site-mode-panel">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-2" data-testid="welcome-site-mode-title">
                    <ShieldCheck className="w-4 h-4 text-emerald-700" /> Site Mode (Owner)
                  </p>
                  <p className="text-xs text-slate-600" data-testid="welcome-site-mode-current-text">
                    Current mode: <span className="uppercase font-semibold">{siteMode}</span>
                  </p>
                  {siteModeUpdatedAt && (
                    <p className="text-[11px] text-slate-500" data-testid="welcome-site-mode-updated-meta-text">
                      Updated by {siteModeUpdatedBy || 'owner'} at {new Date(siteModeUpdatedAt).toLocaleString('en-SG', { hour12: false })}
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={siteMode === 'live' ? 'default' : 'outline'}
                    onClick={() => onSiteModeChange?.('live')}
                    data-testid="welcome-site-mode-live-button"
                  >
                    Set LIVE
                  </Button>
                  <Button
                    type="button"
                    variant={siteMode === 'dev' ? 'default' : 'outline'}
                    onClick={() => onSiteModeChange?.('dev')}
                    data-testid="welcome-site-mode-dev-button"
                  >
                    Set DEV
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {typeof onLogout === 'function' && (
          <div className="flex justify-center mb-8">
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={onLogout}
              data-testid="welcome-logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        )}
        
        <div className={`grid grid-cols-1 ${cardColumnsClass} gap-6`}>

          {canManagePatterns && (
            <Card
              className="cursor-pointer hover:shadow-xl transition-all hover:scale-105 border-2 hover:border-primary"
              onClick={onManagePatterns}
              data-testid="welcome-manage-patterns-card"
            >
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                  <FolderTree className="w-8 h-8 text-amber-700" />
                </div>
                <CardTitle className="text-xl">Manage Patterns</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-sm">
                  Browse by customer and delete versions
                </CardDescription>
              </CardContent>
            </Card>
          )}

          {canAccessUserManagement && (
            <Card
              className="cursor-pointer hover:shadow-xl transition-all hover:scale-105 border-2 hover:border-primary"
              onClick={onUserManagement}
              data-testid="welcome-user-management-card"
            >
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Users className="w-8 h-8 text-indigo-700" />
                </div>
                <CardTitle className="text-xl">User Management</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-sm">
                  Add users and configure role-based access
                </CardDescription>
              </CardContent>
            </Card>
          )}

          {canAccessBackupManagement && (
            <Card
              className="cursor-pointer hover:shadow-xl transition-all hover:scale-105 border-2 hover:border-primary"
              onClick={onBackupManagement}
              data-testid="welcome-backup-management-card"
            >
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-100 flex items-center justify-center">
                  <DatabaseBackup className="w-8 h-8 text-cyan-700" />
                </div>
                <CardTitle className="text-xl">Backup Management</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-sm">
                  Daily backups, restore and retention control
                </CardDescription>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
